import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { useDebugStore } from '../../../../stores/debugStore';
import personalizationService from '../../../../api/personalizationService';
import { getStoredToken, getStoredUser } from '../../../../stores/authStore';

export const usePersonalizationData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toast = useToast();
  
  const {
    setUserSkills,
    setLearningProfile,
    setSkillStatistics,
    showNotifications,
  } = useDebugStore();

  // Fetch user skills
  const fetchUserSkills = useCallback(async () => {
    try {
      const skills = await personalizationService.getUserSkills();
      setUserSkills(skills.map(skill => ({
        skill_id: skill.skill_id,
        skill_code: skill.skill_code,
        skill_name: skill.skill_name,
        proficiency: skill.proficiency,
        confidence: skill.confidence,
        last_practiced: skill.last_practiced,
        total_attempts: skill.total_attempts,
        successful_attempts: skill.successful_attempts,
      })));
      
      if (showNotifications) {
        toast({
          title: 'Skills loaded',
          description: `Loaded ${skills.length} skills`,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } catch (error: any) {
      setErrors(prev => ({ ...prev, skills: error.message }));
      console.error('Failed to fetch user skills:', error);
    }
  }, [setUserSkills, showNotifications, toast]);

  // Fetch skill statistics
  const fetchSkillStatistics = useCallback(async () => {
    try {
      const stats = await personalizationService.getSkillStatistics();
      setSkillStatistics({
        total_tracked: stats.total_skills,
        mastered: stats.mastered,
        learning: stats.learning,
        struggling: stats.struggling,
        needs_review: stats.review_needed?.length || 0,
      });
    } catch (error: any) {
      setErrors(prev => ({ ...prev, statistics: error.message }));
      console.error('Failed to fetch skill statistics:', error);
    }
  }, [setSkillStatistics]);

  // Fetch learning profile
  const fetchLearningProfile = useCallback(async () => {
    try {
      // First get the basic profile
      const profile = await personalizationService.getUserProfile();
      
      // Then get personalization summary
      const summary = await personalizationService.getPersonalizationSummary();
      
      // Combine the data
      setLearningProfile({
        learning_style: profile?.learning_style,
        learner_summary: profile?.learner_profile_summary,
        knowledge_domains: profile?.knowledge_domains,
        interests: summary?.interests?.map(i => i.topic) || [],
        grade_level: profile?.current_grade_level,
        occupation: profile?.occupation,
        favorite_subject: profile?.favorite_subject_and_reasoning,
        challenges: profile?.challenging_subjects_areas,
      });
    } catch (error: any) {
      setErrors(prev => ({ ...prev, profile: error.message }));
      console.error('Failed to fetch learning profile:', error);
    }
  }, [setLearningProfile]);

  // Fetch learning journey
  const fetchLearningJourney = useCallback(async () => {
    try {
      const journey = await personalizationService.getLearningJourney();
      // Process journey data if needed
      // For now, we'll just log it
      console.log('Learning journey:', journey);
    } catch (error: any) {
      setErrors(prev => ({ ...prev, journey: error.message }));
      console.error('Failed to fetch learning journey:', error);
    }
  }, []);

  // Analyze profile deprecated: no-op retained for compatibility

  // Refresh all data
  const refreshAll = useCallback(async () => {
    setIsLoading(true);
    setErrors({});
    
    try {
      await Promise.all([
        fetchUserSkills(),
        fetchSkillStatistics(),
        fetchLearningProfile(),
        fetchLearningJourney(),
      ]);
    } catch (error) {
      console.error('Failed to refresh all data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchUserSkills, fetchSkillStatistics, fetchLearningProfile, fetchLearningJourney]);

  // Auto-refresh on mount
  useEffect(() => {
    const autoRefresh = useDebugStore.getState().autoRefresh;
    if (!autoRefresh) return;

    let cancelled = false;
    let attempts = 0;

    const isAuthReady = () => {
      try {
        const token = getStoredToken() || localStorage.getItem('token');
        const user = getStoredUser() || (JSON.parse(localStorage.getItem('user') || 'null') as any);
        if (!token || !user) return false;
        return !!(user.id || user.user_id);
      } catch {
        return false;
      }
    };

    const tryRefresh = async () => {
      if (cancelled) return;
      if (isAuthReady()) {
        await refreshAll();
        return;
      }
      attempts += 1;
      if (attempts <= 5) {
        // Retry a few times to avoid race with AuthContext initialization
        setTimeout(tryRefresh, 700);
      } else {
        console.warn('[PersonalizationDebug] Skipping auto-refresh: auth not ready');
      }
    };

    tryRefresh();
    return () => {
      cancelled = true;
    };
  }, [refreshAll]);

  return {
    isLoading,
    errors,
    fetchUserSkills,
    fetchSkillStatistics,
    fetchLearningProfile,
    fetchLearningJourney,
    refreshAll,
  };
};
